import React, { useCallback, useEffect } from 'react';

import { useAppSelector } from 'hooks/store';
import { ICallScribeCallListItem } from 'services/callScribe/types';
import { CallScribeAccessSelector } from 'store/callScribe/selectors';

import DialBar from 'components/CallScribe/Home/DialBar';
import RecentsList from 'components/CallScribe/Recents/RecentsList';
import useHomeStyles from 'components/CallScribe/Home/homeStyles';
import { useTicketPodData } from 'components/CallScribe/Home/useTicketPodData';
import { usePlaceOutbound } from 'components/CallScribe/Home/usePlaceOutbound';
import { isEmbeddedInIframe } from 'components/CallScribe/popout';
import { IUseTwilioDeviceResult } from 'components/CallScribe/useTwilioDevice';

export type TDialLifecycleEvent =
  | {
      type: 'started';
      callId: number;
      to: string;
      contactName?: string;
      documentationNotice?: string;
    }
  | { type: 'failed'; to: string; callId?: number; code?: number; message?: string };

export interface ICallScribeHomeProps {
  calls: ICallScribeCallListItem[];
  loading: boolean;
  error?: string;
  onRetry?: () => void;
  twilio: IUseTwilioDeviceResult;
  onDialLifecycle?: (event: TDialLifecycleEvent) => void;
  onDialReady?: (
    redial: (to: string, contactExternalId?: string, contactName?: string) => void,
  ) => void;
  onTicketContact?: (contactName: string) => void;
}

const CallScribeHome: React.FC<ICallScribeHomeProps> = ({
  calls,
  loading,
  error,
  onRetry,
  twilio,
  onDialLifecycle,
  onDialReady,
  onTicketContact,
}) => {
  const classes = useHomeStyles();
  const access = useAppSelector(CallScribeAccessSelector);
  const ticket = useTicketPodData();
  const { podData } = ticket;
  const { placeOutbound, dialFailed, busyWith } = usePlaceOutbound({
    twilio,
    companyId: podData.CompanyId,
    onDialLifecycle,
  });

  useEffect(() => {
    onDialReady?.(placeOutbound);
  }, [onDialReady, placeOutbound]);

  useEffect(() => {
    onTicketContact?.((podData.ContactName ?? '').trim());
  }, [onTicketContact, podData.ContactName]);

  const onRedial = useCallback((phone: string, contactExternalId?: string) => {
    placeOutbound(phone, contactExternalId);
  }, [placeOutbound]);

  const redialDisabled = isEmbeddedInIframe()
    ? false
    : twilio.state !== 'ready' || !!twilio.activeCall;

  return (
    <div className={classes.root}>
      <DialBar
        calls={calls}
        twilio={twilio}
        ticket={ticket}
        placeOutbound={placeOutbound}
        dialFailed={dialFailed}
        busyWith={busyWith}
      />

      <RecentsList
        calls={calls}
        loading={loading}
        error={error}
        onRetry={onRetry}
        onRedial={onRedial}
        redialDisabled={redialDisabled}
        seesOnlyOwnCalls={access?.seesOnlyOwnCalls}
      />
    </div>
  );
};

export default CallScribeHome;
